import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag, X, CheckCircle2, LogIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, deliveryFee, tax, total: cartTotal, systemConfig } = useCart();
  const { user, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [suggested, setSuggested] = useState([]);

  useEffect(() => { api.get('/coupons').then((r) => setSuggested(r.data)).catch(() => {}); }, []);

  // Auto-apply welcome coupon set by onboarding
  useEffect(() => {
    if (!user || coupon || subtotal <= 0) return;
    try {
      const stored = sessionStorage.getItem('pushpanjali_coupon');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (parsed?.code) applyCoupon(parsed.code);
    } catch {}
    // eslint-disable-next-line
  }, [user, subtotal]);

  // Keep sessionStorage in sync with applied coupon
  useEffect(() => {
    if (coupon) {
      sessionStorage.setItem('pushpanjali_coupon', JSON.stringify(coupon));
    } else {
      sessionStorage.removeItem('pushpanjali_coupon');
    }
  }, [coupon]);

  const applyCoupon = async (codeToApply) => {
    const code = (codeToApply || couponCode).trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.post('/coupons/validate', { code, subtotal });
      setCoupon(data); setCouponCode(code);
      toast.success(`${code} applied — saved ₹${data.discount}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid coupon'); }
  };

  const discount = coupon?.discount || 0;
  const total = Math.max(cartTotal - discount, 0);

  const isOfflineAndNoPreorders = !systemConfig?.is_currently_open && !systemConfig?.allow_preorders;
  const isBelowMinOrder = subtotal < (systemConfig?.min_order_value ?? 0);

  const getCartStatusMessage = () => {
    if (!systemConfig) return null;
    
    const isOnline = systemConfig.is_online;
    const allowPreorders = systemConfig.allow_preorders;
    const isCurrentlyOpen = systemConfig.is_currently_open;
    
    if (isCurrentlyOpen) return null;
    
    if (isOnline === false && !allowPreorders) {
      return {
        text: 'Kitchen is offline. Please check after some time.',
        type: 'error'
      };
    }
    
    if (isOnline === false && allowPreorders) {
      return {
        text: 'Kitchen is offline but you can preorder food.',
        type: 'warning'
      };
    }
    
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000));
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[istDate.getDay()];
    const isDayOperational = systemConfig.operating_hours?.[currentDay]?.is_open;
    
    if (!isDayOperational) {
      const days_ordered = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const operationalDaysList = days_ordered
        .filter(day => systemConfig.operating_hours?.[day]?.is_open)
        .map(day => day.charAt(0).toUpperCase() + day.slice(1));
      return {
        text: `We only serve on ${operationalDaysList.join(', ')}.`,
        type: allowPreorders ? 'warning' : 'error'
      };
    }
    
    if (allowPreorders) {
      return {
        text: `🔔 We are closed, but accepting pre-orders for ${systemConfig.next_opening || 'soon'}. Choose delivery slot at checkout.`,
        type: 'warning'
      };
    } else {
      return {
        text: `We are currently offline. We will resume ordering on ${systemConfig.next_opening || 'our scheduled hours'}.`,
        type: 'error'
      };
    }
  };

  const proceed = () => {
    const status = getCartStatusMessage();
    if (status && status.type === 'error') {
      toast.error(status.text);
      return;
    }
    if (isBelowMinOrder) {
      toast.error(`Minimum order value to checkout is ₹${systemConfig?.min_order_value}. Please add more items.`);
      return;
    }
    if (!user) { toast.info('Please sign in to continue'); loginWithGoogle(); return; }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="pb-24 md:pb-8 max-w-2xl mx-auto px-4 md:px-6 py-16 text-center">
        <ShoppingBag className="w-16 h-16 text-saffron mx-auto mb-4" />
        <h2 className="font-heading font-bold text-2xl text-textprimary mb-2">Your cart is empty</h2>
        <p className="text-textsecondary font-body mb-6">Add items to cart to get started</p>
        <Link to="/menu" data-testid="cart-browse-menu" className="inline-flex items-center gap-2 bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-semibold shadow-warm">
          Add Items <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-32 md:pb-8 max-w-4xl mx-auto px-4 md:px-6 py-6">
      <h1 className="font-heading font-extrabold text-3xl text-textprimary mb-6">Your Cart</h1>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-3">
          <AnimatePresence>
            {items.map((i) => (
              <motion.div key={i.item_id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }} className="bg-white rounded-2xl p-3 flex gap-3 shadow-card border border-borderbeige" data-testid={`cart-item-${i.item_id}`}>
                <img src={i.image_url} alt={i.name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-textprimary text-sm">{i.name}</h3>
                  <p className="text-maroon font-heading font-bold mt-1">₹{i.price}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 bg-bgsecondary rounded-full p-1">
                      <button onClick={() => updateQty(i.item_id, i.quantity - 1)} data-testid={`cart-decrease-${i.item_id}`} className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Minus className="w-3 h-3 text-saffron" /></button>
                      <span className="text-xs font-heading font-bold min-w-[16px] text-center">{i.quantity}</span>
                      <button 
                        onClick={() => {
                          if (isOfflineAndNoPreorders) {
                            toast.error('Kitchen is offline. Cannot add more items.');
                            return;
                          }
                          updateQty(i.item_id, i.quantity + 1);
                        }} 
                        data-testid={`cart-increase-${i.item_id}`} 
                        disabled={isOfflineAndNoPreorders}
                        className="w-6 h-6 rounded-full bg-saffron flex items-center justify-center disabled:opacity-40"
                      >
                        <Plus className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(i.item_id)} data-testid={`cart-remove-${i.item_id}`} className="text-textlight hover:text-maroon"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Inline Coupon */}
          <div className="bg-white rounded-2xl p-4 shadow-card border border-borderbeige">
            <h3 className="font-heading font-bold text-sm mb-2 flex items-center gap-2 text-textprimary"><Tag className="w-4 h-4 text-saffron" /> Apply Coupon</h3>
            {coupon ? (
              <div className="bg-fresh/10 text-fresh rounded-xl px-3 py-2 flex justify-between items-center text-sm font-body" data-testid="cart-applied-coupon">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /><b>{coupon.code}</b> saved ₹{coupon.discount}</span>
                 <button onClick={() => { setCoupon(null); setCouponCode(''); }}><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                {!user && <p className="text-xs text-textsecondary mb-2 font-body">💡 Sign in to apply coupons</p>}
                <div className="flex gap-2">
                  <input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" data-testid="cart-coupon-input" disabled={!user} className="flex-1 px-3 py-2 border border-borderbeige rounded-xl text-sm disabled:bg-bgsecondary disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-saffron/40" />
                  <button onClick={() => user ? applyCoupon() : loginWithGoogle()} data-testid="cart-apply-coupon" className="bg-maroon text-white px-4 rounded-xl text-sm font-heading font-semibold">{user ? 'Apply' : 'Sign in'}</button>
                </div>
                {suggested.length > 0 && (
                  <div className="mt-2 flex gap-1 overflow-x-auto no-scrollbar">
                    {suggested.slice(0, 3).map((c) => (
                      <button key={c.code} onClick={() => user ? applyCoupon(c.code) : loginWithGoogle()} data-testid={`cart-suggest-${c.code}`} className="shrink-0 text-[10px] bg-golden/20 text-maroon px-2 py-1 rounded-full font-heading font-semibold flex items-center gap-1"><Tag className="w-3 h-3" />{c.code}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="md:sticky md:top-24 h-fit bg-white rounded-2xl p-5 shadow-card border border-borderbeige">
          <h2 className="font-heading font-bold text-lg text-textprimary mb-4">Bill Summary</h2>
          <div className="space-y-2 font-body text-sm text-textprimary">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">₹{subtotal.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between text-fresh"><span>Coupon</span><span>−₹{discount.toFixed(2)}</span></div>}
            {systemConfig?.show_tax_and_delivery && (
              <>
                <div className="flex justify-between"><span>Delivery</span><span>₹{deliveryFee.toFixed(2)}</span></div>
                <div className="flex justify-between text-textsecondary text-xs"><span>Tax ({systemConfig?.tax_percentage ?? 5}%)</span><span>₹{tax.toFixed(2)}</span></div>
              </>
            )}
            <div className="border-t border-borderbeige my-3" />
            <div className="flex justify-between font-heading font-bold text-lg">
              <span>Total</span><span className="text-maroon" data-testid="cart-total">₹{total.toFixed(2)}</span>
            </div>
          </div>
          <button 
            onClick={proceed} 
            disabled={isOfflineAndNoPreorders || isBelowMinOrder}
            data-testid="cart-checkout-btn" 
            className="mt-5 w-full bg-saffron text-white py-3 rounded-2xl font-heading font-bold hover:bg-saffron-dark shadow-warm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed animate-fade-in"
          >
            {isOfflineAndNoPreorders 
              ? 'Kitchen Offline' 
              : isBelowMinOrder 
                ? `Min. Order ₹${systemConfig?.min_order_value} Required` 
                : user 
                  ? 'Proceed to Checkout' 
                  : 'Sign in to Checkout'
            }
          </button>
          {(() => {
            const status = getCartStatusMessage();
            if (!status) return null;
            if (isBelowMinOrder && status.type === 'warning') return null;
            return (
              <p className={`text-xs text-center mt-2 font-body font-semibold leading-relaxed ${
                status.type === 'error' ? 'text-red-600' : 'text-amber-600'
              }`}>
                {status.text}
              </p>
            );
          })()}
          {isBelowMinOrder && !isOfflineAndNoPreorders && (
            <p className="text-xs text-maroon text-center mt-2 font-body font-semibold leading-relaxed">
              Add ₹{((systemConfig?.min_order_value || 0) - subtotal).toFixed(2)} more to place order.
            </p>
          )}
          {user && !isOfflineAndNoPreorders && !isBelowMinOrder && (
            <p className="text-xs text-textlight text-center mt-2 font-body">Add address & phone on the next step</p>
          )}
        </div>
      </div>
    </div>
  );
}
